<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, toRef, watch } from "vue"
import { storeToRefs } from "pinia"
import { RouterView, useRoute } from "vue-router"
import { useI18n } from "vue-i18n"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { getRoutePageTitle } from "@core/composables/useDocumentTitle"
import { providePvPageShell } from "@modules/pv/composables/usePvPageShell"
import { usePvStore } from "@modules/pv/stores/pv.store"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            /** Titre si le `name` de route n’est pas encore une sous-page PV connue. */
            defaultHeaderTitle: "Photovoltaïque",
        },
    },
})

// ================================
// COMPOSABLES
// ================================
const route = useRoute()
const shell = providePvPageShell()
const pvStore = usePvStore()
const buildingStore = useBuildingStore()
const { sourceId } = storeToRefs(pvStore)
const { isFetchingBuildingSources } = storeToRefs(buildingStore)

// ================================
// DONNÉES CALCULÉES
// ================================
/** `name` de l’enfant le plus spécifique (routes imbriquées sous `pv`). */
const getLeafMatchedRouteName = computed(
    () => route.matched[route.matched.length - 1]?.name ?? route.name
)

/** Aligné sur {@link getRoutePageTitle} / onglet navigateur pour la sous-route PV active. */
const getHeaderTitle = computed(() => {
    const fromRoute = getRoutePageTitle(getLeafMatchedRouteName.value)
    return fromRoute.trim().length > 0 ? fromRoute : t("defaultHeaderTitle")
})

const sourceAppendDialogVisible = toRef(shell, "sourceAppendDialogVisible")
const sourceAppendDialogKey = shell.sourceAppendDialogKey
const sourceAppendFromState = toRef(shell, "sourceAppendFromState")
const dialogBuildingId = shell.getBuildingId
const etudeEcoDialogVisible = toRef(shell, "etudeEcoDialogVisible")
const etudeEcoDimensionnementId = toRef(shell, "etudeEcoDimensionnementId")
const pvDimensionnementDialogVisible = toRef(
    shell,
    "dimensionnementDialogVisible"
)
const currentDimensionnementForm = shell.getCurrentDimensionnementForm
const getPvDialogSourceId = shell.getSourceId
const getPvDialogSegment = shell.getCurrentSegment
const getPvDialogAnalyseId = shell.getCurrentDimensionnementAnalyseId
const getPvDialogExtrapolationId = shell.getExtrapolationId

/**
 * Clé stable bâtiment + source PDL (défaut) une fois les sources chargées —
 * alignée sur les sous-pages PV pour éviter un chargement avant disponibilité des PDL.
 *
 * La résolution passe par {@link pvStore.getResolvedSourceIdForCurrentBuilding}
 * (DEV-378) : valide `sourceId` contre les PDLs du bâtiment courant, sinon
 * fallback sur le 1er PDL pour ne pas hériter d'un sourceId d'un autre bâtiment.
 */
const getPvStudiesLoadKey = computed((): string | null => {
    const bid = shell.getBuildingId.value
    if (!bid) return null
    if (isFetchingBuildingSources.value) return null
    if (buildingStore.hasErrorBuildingSources) return null
    const sid = pvStore.getResolvedSourceIdForCurrentBuilding()
    if (!sid) return null
    return `${bid}:${sid}`
})

/**
 * Historique de dimensionnement pour l’en-tête et les sous-routes.
 * Le `GET …/last` est réservé à `PageBuildingPVDimensionnement` (évite un appel inutile sur les autres onglets).
 */
async function loadPvStudiesContext(): Promise<void> {
    const bid = shell.getBuildingId.value
    if (!bid) return
    if (isFetchingBuildingSources.value) return
    if (buildingStore.hasErrorBuildingSources) return

    await pvStore.fetchBootstrapBuildingSources()
    if (pvStore.hasErrorBootstrapBuildingSources) return

    const sid = pvStore.getResolvedSourceIdForCurrentBuilding()
    if (!sid) {
        pvStore.resetPvDerivedContext()
        pvStore.setSourceId(null, { fetchSourcePv: false })
        return
    }
    if (sourceId.value !== sid) {
        pvStore.setSourceId(sid, { fetchSourcePv: false })
    }
    const resolvedSourceId = sourceId.value
    if (!resolvedSourceId) return

    const ids = { sourceId: resolvedSourceId, buildingId: bid }
    await pvStore.fetchDimensionnementHistoryBySource(ids)
}

/**
 * Périmètre bâtiment de l'état PV (DEV-378, DEV-2196). Sans lui, `pv.sourceId`,
 * `sourcePvData` et l'extrapolation héritent du bâtiment précédent lors d'une
 * navigation SPA : le header affiche le bon PDL (lu depuis `buildingStore`) mais
 * graph + dialog d'export utilisent le state hérité → mauvais PRM exporté.
 * `immediate` : le store survit au démontage de cette page, le contrôle doit donc
 * aussi tenir au montage (arrivée depuis une autre page d'un autre bâtiment), et
 * passer AVANT le chargement des études ci-dessous.
 */
watch(
    () => shell.getBuildingId.value,
    (bid) => {
        pvStore.ensureBuildingScope(bid ?? null)
    },
    { immediate: true }
)

watch(
    getPvStudiesLoadKey,
    (key) => {
        if (!key) return
        void loadPvStudiesContext()
    },
    { immediate: true }
)
</script>

<template>
    <div class="page page-building-pv-wrapper mx-auto w-full">
        <HeaderPageBuilding
            :title="getHeaderTitle"
            icon="pi pi-sun"
            class-header="flex flex-col gap-3 w-full"
        >
            <HeaderPvBuilding
                v-if="shell.getBuildingId"
                @click:create-dimensionnement="
                    shell.onClickCreateDimensionnement
                "
                @click:add-pdl="shell.onClickAddPdl"
                @click:modal-etude-eco="shell.onClickModalEtudeEco"
            />
        </HeaderPageBuilding>

        <main class="mt-3">
            <RouterView />
        </main>

        <SourceAppendDialog
            v-model:visible="sourceAppendDialogVisible"
            :key="sourceAppendDialogKey"
            :building-id="dialogBuildingId"
            :from-state="sourceAppendFromState"
            :allowed-source-types="['pdl', 'pce']"
            :address="shell.buildingStore.building?.address ?? null"
            @done="shell.onSourceAppendDone"
            @cancel="shell.onSourceAppendCancel"
        />
        <PVDialogDimensionnement
            v-model:visible="pvDimensionnementDialogVisible"
            :source-id="getPvDialogSourceId"
            :segment="getPvDialogSegment"
            :analyse-id="getPvDialogAnalyseId"
            :id-building="dialogBuildingId"
            :id-extrapolation="getPvDialogExtrapolationId"
            :form="currentDimensionnementForm"
            @close="shell.onCloseDimensionnementDialog"
            @create:analyse="shell.onCreateDimensionnement"
            @update:analyse="shell.onUpdateDimensionnement"
        />
        <PVEtudeEcoDialog
            v-model:visible="etudeEcoDialogVisible"
            :dimensionnement-id="etudeEcoDimensionnementId"
            :segment="getPvDialogSegment"
            @done="shell.onEtudeEcoDialogDone"
        />
        <BaseConfirmDialog class="max-w-[30rem] w-full" />
        <BaseToast />
    </div>
</template>
